import { makeThemedStyles } from "@qpr/client-api-base/viewcomponents/utils";
import flattenHierarchy from "backend/flattenHierarchy";
import { ICardElement, ICardElementWithChildren } from "backend/model/model";
import { t } from "i18next";
import React from "react";
import HighChart from "./Highchart";
import { IChartSelectEvent } from "./types";

const useStackedKpiInitiativeStyles = makeThemedStyles(
  "StackedKpiInitiative",
  (theme) => ({
    chartTitleStyle: {
      fontFamily: "Roboto, Helvetica",
      fontSize: "1.3em",
      transform: "translateY(-20px)",
    },
  })
);

export const StackedKpiInitiative = React.memo(
  (
    props: React.PropsWithChildren<{
      element: ICardElementWithChildren[];
      perfNameKey: Exclude<keyof ICardElement, "period" | "workflowState">;
      perfColorKey: Exclude<keyof ICardElement, "period" | "workflowState">;
      typenames: string[];
      width?: number;
      chartType?: string;
      height?: string | number;
      onSelect?: (value: IChartSelectEvent) => void;
    }>
  ) => {
    const styles = useStackedKpiInitiativeStyles();
    const { element: el, perfNameKey, perfColorKey, typenames } = props;
    const list = React.useMemo(() => {
      return flattenHierarchy(el);
    }, [el]);
    const chartType = props.chartType || "column";

    const rangeNames: {
      name: string;
      color: string;
      cnt: Record<string, number>;
    }[] = [];
    const rangeStyles: any = {};
    // range names for each type
    typenames.forEach((value) => {
      list
        .filter(
          (el) => el.typename.toLocaleLowerCase() === value.toLocaleLowerCase()
        )
        .forEach((item) => {
          const rangeName = item[perfNameKey] || "No Value";
          const rangeColor = item[perfColorKey] || "000000";
          if (rangeName) {
            if (!rangeNames.find((r) => r.name === rangeName)) {
              const obj = rangeNames.push({
                name: rangeName as string,
                color: rangeColor as string,
                cnt: {
                  [value]: 1,
                },
              });
              rangeStyles[
                `--range-${rangeName}`
              ] = `linear-gradient(to right, #${rangeColor}11, #${rangeColor}22, #${rangeColor}11)`;
            } else {
              const r = rangeNames.find((r) => r.name === rangeName);
              if (r) {
                r.cnt[value] = r.cnt[value] ? r.cnt[value] + 1 : 1;
              }
            }
          }
        });
    });

    //     for (let i = 0; i < list; i++) {}

    const hcOptions = React.useMemo(() => {
      if (!el) {
        return {};
      }
      return {
        credits: {
          enabled: false,
        },
        chart: {
          type: "column",
          width: 300,
          height: 300,
          backgroundColor: "rgba(255,255,255,0)",
        },
        title: null,
        xAxis: {
          gridLineColor: "transparent",
          categories: typenames,
        },
        yAxis: {
          gridLineColor: "transparent",
          min: 0,
          title: {
            text: "",
          },
        },
        legend: {
          enabled: false,
          align: "left",
          x: 70,
          verticalAlign: "top",
          y: 70,
          floating: true,
          backgroundColor: "rgba(255,255,255,0)",
          borderColor: "#CCC",
          borderWidth: 1,
          shadow: false,
        },
        tooltip: {
          headerFormat: "<b>{point.x}</b><br/>",
          pointFormat: "{series.name}: {point.y}<br/>Total: {point.stackTotal}",
        },
        plotOptions: {
          column: {
            stacking: "normal",
            dataLabels: {
              enabled: true,
            },
          },
        },
        series: [
          ...rangeNames.map((range) => {
            return {
              name: range.name,
              shadow: {
                color: `#${range.color || "gray"}44`,
                opacity: 0.3,
                width: 4,
              },
              events: {
                click: function (this: any, event: any) {
                  if (props.onSelect) {
                    props.onSelect({
                      category: event.point.category,
                      index: this.index,
                      name: this.name,
                      items: props.element,
                    });
                  }
                },
              },
              color: {
                linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
                stops: [
                  [0, `#${range.color || "gray"}ff`],
                  [1, `#${range.color || "gray"}99`],
                ],
              },
              width: 20,
              borderColor: "white",
              borderWidth: 0,
              borderRadius: 1,
              data: typenames.map((tn) => {
                return range.cnt[tn] || 0;
              }),
            };
          }),
        ],
      };
    }, [el]);

    if (!el) return null;
    return <HighChart chartOptions={hcOptions} />;
  }
);
